<template>
  <div :key="`indicators-${targetId}`" class="indicator-list">
    <sortable-list
      v-if="creatable || indicatorLength > 0"
      :items="sortedIndicators"
      :sortable="editable"
      order-by="order"
      class="mb-2"
      line-class="line indicator-list__entry"
      @update:order="updateOrder($event)"
    >
      <template
        #default="{
          item: indicator,
          index
        }: {
          item: IndicatorItem;
          index: number;
        }"
      >
        <span class="indicator-list__counter">{{ numToLetter(index) }}</span>
        <div class="indicator-list__text">
          <input-text
            v-if="editable"
            :value="indicator.title"
            :placeholder="$t('labels.unnamed')"
            :growing="true"
            class="-not-draggable"
            @update:value="updateKey(indicator, 'title', $event)"
          >
            <template v-if="deletable" #buttons>
              <button
                class="button-icon input__icon"
                type="button"
                @click="indicatorStore.delete(indicator)"
              >
                <lucide-icon name="trash-2" />
              </button>
            </template>
          </input-text>
          <div v-if="!editable">
            <span>{{ indicator.title }}</span>
            <formatted-text
              v-if="
                !editable && indicator.docs.description && showDocumentation
              "
              :value="indicator.docs.description"
              class="comment"
            />
          </div>
        </div>
        <documentation-anchor
          v-if="editable"
          class="indicator-list__doc-anchor"
          :object="indicator"
          prop="description"
        />
      </template>

      <template v-if="creatable" #after-list>
        <input-create
          :key="'indicatortext' + targetId"
          :placeholder="`+ ${$t('labels.newIndicator')}`"
          @submit="addNew"
        />
      </template>
    </sortable-list>

    <p v-if="creatable === false && indicatorLength === 0" class="message mt-0">
      {{ $t("errors.noIndicators") }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { sortBy } from "lodash-es";
import { numberToLetter } from "@/helpers/utility/NumberToLetter";

import InputText from "@core/input/InputText.vue";
import InputCreate from "@core/input/InputTextCreate.vue";
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import SortableList from "@core/ui/SortableList.vue";

import { useIndicatorStore } from "@editor/items/indicator/indicators.store";
import type { IndicatorItem } from "./IndicatorItem";

interface IndicatorListProps {
  targetId: number;
  creatable?: boolean;
  deletable?: boolean;
  editable?: boolean;
  showDocumentation?: boolean;
}

const props = withDefaults(defineProps<IndicatorListProps>(), {
  creatable: true,
  deletable: true,
  editable: true,
  showDocumentation: false
});

const indicatorStore = useIndicatorStore();

const targetIndicators = computed(
  () => indicatorStore.byTarget[props.targetId] ?? []
);
const sortedIndicators = computed(() =>
  sortBy(targetIndicators.value, "order")
);
const indicatorLength = computed(() => targetIndicators.value.length);

function addNew(title: string) {
  indicatorStore.create({
    targetId: props.targetId,
    title,
    order: indicatorLength.value
  });
}

function updateOrder(itemOrder: number[]) {
  const saveData = Object.values(itemOrder).map((indicatorId, order) => {
    return {
      id: indicatorId,
      order
    };
  });
  indicatorStore.updateMany(saveData);
}

function numToLetter(num: number) {
  return numberToLetter(num).toUpperCase();
}

function updateKey<K extends keyof IndicatorItem>(
  indicator: { id: number },
  key: K,
  value: IndicatorItem[K]
) {
  indicatorStore.update({
    id: indicator.id,
    [key]: value
  });
}
</script>

<style lang="scss">
.indicator-list {
  min-height: 2rem;
  padding: 0;

  &__entry {
    display: flex;
  }
  &__counter {
    width: 2em;
    text-align: center;
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    font-weight: 600;
    color: var(--color-text-medium);
  }
  &__text {
    width: 100%;
    position: relative;
    min-width: 0;
  }
  &__doc-anchor {
    padding-left: 0.5em;
  }
}
</style>
